import React, {useEffect, useMemo, useState, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {actions} from '../../store';
import {ReduxState} from '../../store/types';
import {useTranslation} from 'react-i18next';
import EntryModal from './EntryModal';
import {PhoneBookListItem} from '../../store/reducers/phoneBook/reducer';
import AlertDialog from '../../components/AlertDialog/AlertDialog';
import {DialogButton} from '../../components/AlertDialog/DialogContainer';
import CallRemoveBottomBox from '../../components/Calls/CallRemoveBottomBox';
import {PhoneBookListItemExtended} from './EntryModal';
import DataGrid from '../../components/DataGrid/DataGrid';
import {useStyles, generateColumns, generateSvColumns} from './utils';
import Header from '../../components/ListViewHeader/Header';
import usePageTitle from '../../hooks/usePageTitle';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import {Permission, PermissionType} from '../../store/types/Permission';
import {usePermissions} from '../../hooks/usePermissions';
import PermissionPlaceholder from '../../components/PermissionProvider/PermissionPlaceholder';
import { PaginationMode } from '../../components/DataGrid/types';
import {useIndividualTypeHook} from "../../hooks/agents/useIndividualTypeHook";

export const PhoneBookList = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    usePageTitle();
    const {isSupervisor} = useIndividualTypeHook()

    const deletePhoneBookPermission = usePermissions(
        ...Permission.CloudPBX.PhoneBook.DeletePhoneBookEntry.value,
    );

    const [entryModalVisible, setEntryModalVisible] = useState<boolean>(false);
    const [removeDialogVisible, setRemoveDialogVisible] = useState<boolean>(
        false,
    );
    const [
        editedRow,
        setEditedRow,
    ] = useState<PhoneBookListItemExtended | null>(null);
    const [rowToDelete, setRowToDelete] = useState<PhoneBookListItem | null>(
        null,
    );

    const [selectedItems, setSelectedItems] = useState<PhoneBookListItem[]>([]);
    const [forceFirstPage, setForceFirstPage] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);

    const {phonesBookList, isLoading} = useSelector(
        (state: ReduxState) => state.phoneBook,
    );
    const {t} = useTranslation();

    const fetchData = (params?: {
        pageSize: number;
        page: number;
    }) => {
        dispatch(actions.getPhoneBookList.request({
            limit: params ? params.pageSize : pageSize,
            offset: params ? params.page * params.pageSize : page,
        }));
        dispatch(actions.getMaxAbbreviated.request());
    };

    const setAddEntryModalVisible = (row: PhoneBookListItemExtended | null) => {
        setEditedRow(row);
        setEntryModalVisible(true);
    };

    const closeRemoveModal = () => {
        setRemoveDialogVisible(false);
    };

    const deletePhoneBooks = () => {
        const itemsToDelete: number[] = [];
        selectedItems.forEach((element) => {
            itemsToDelete.push(element.id);
        });
        dispatch(
            actions.deletePhoneBook.request({
                itemsToDelete,
                callback: fetchData
            }),
        );

        if (removeDialogVisible) {
            setSelectedItems([]);
            setRemoveDialogVisible(!removeDialogVisible);
        } else {
            setSelectedItems([]);
        }
    };

    const deleteSinglePhoneBook = (itemToDelete: PhoneBookListItem) => {
        const itemsToDelete: number[] = [itemToDelete?.id];
        dispatch(
            actions.deletePhoneBook.request({
                itemsToDelete,
                callback: fetchData
            }),
        );

        setRemoveDialogVisible(!removeDialogVisible);
        setEditedRow(null);
    };

    const removeEntry = (itemToDelete: PhoneBookListItem) => {
        setRowToDelete(itemToDelete);
        setRemoveDialogVisible(true);
    };

    const columns = useMemo(
        () => {

            if(isSupervisor)
            {
                return generateSvColumns(t, setAddEntryModalVisible, removeEntry, classes)

            }else{
                return generateColumns(t, setAddEntryModalVisible, removeEntry, classes)

            }
        },
        [],
    );

    useEffect(() => {
        fetchData();
    }, []);

    const onChangeItemsSelection = useCallback(
        (items) => setSelectedItems(items),
        [],
    );

    return (
        <PermissionPlaceholder permission={Permission.CloudPBX.PhoneBook.value}>
            <div className={classes.mainContainer}>
                <div className={classes.scrollable}>
                    <div className={classes.header}>
                        <PermissionProvider
                            permission={
                                Permission.CloudPBX.PhoneBook
                                    .AddNewPhoneBookEntry.value
                            }
                        >
                            <Header
                                title={t('screens:phoneBook.phoneBook')}
                                totalNumber={phonesBookList.total || 0}
                                showDetails={false}
                                dataQa={'phone-book-button'}
                                buttonText={t('screens:phoneBook.addEntry')}
                                ofText={t('screens:autoAttendants.disabled')}
                                buttonOnClick={() =>
                                    setAddEntryModalVisible(null)
                                }
                                buttonDataQa={'add-new-phone-book-button'}
                                plusIcon={true}
                            />
                        </PermissionProvider>
                    </div>

                    <DataGrid<PhoneBookListItemExtended>
                        columns={columns}
                        data={phonesBookList?.items}
                        rowCount={phonesBookList.total}
                        loading={isLoading}
                        onChangeItemsSelection={onChangeItemsSelection}
                        centeredRows
                        multiSelectEnabled={
                            !isSupervisor && deletePhoneBookPermission === PermissionType.Visible
                        }
                        narrowRows
                        classes={{
                            tableContainer:
                                (deletePhoneBookPermission ===
                                PermissionType.Visible && !isSupervisor)
                                    ? classes.tableContainerWithMultiSelect
                                    : classes.tableContainer,
                        }}
                        customRowHeight={55}
                        onPageChange={(params) => {
                            setForceFirstPage(false);
                            fetchData({
                                pageSize: params.pageSize,
                                page: params.page,
                            });
                            setPage(params.page);
                        }}
                        onPageSizeChange={(params) => {
                            setForceFirstPage(false);
                            setPageSize(params.pageSize);
                            setPage(0);
                            fetchData({
                                pageSize: params.pageSize,
                                page: 0,
                            });
                        }}
                        paginationMode={PaginationMode.Server}
                        forceFirstPage={forceFirstPage}
                        initialPageSize={10}
                    />
                    <EntryModal
                        isOpen={entryModalVisible}
                        toggleVisibility={(isSaved) => {
                            setEntryModalVisible(!entryModalVisible);
                            if(isSaved) {
                                fetchData();
                            }
                        }}
                        editedRow={editedRow}
                        isSupervisor={isSupervisor}
                    />

                    <AlertDialog
                        isOpen={removeDialogVisible}
                        dataQa={'delete-phone-book-modal'}
                        hideHeader={true}
                        className={classes.containerDialog}
                        description={
                            selectedItems.length > 1 ? (
                                t(
                                    'screens:phoneBook.deletePhoneBooksDescription',
                                    {
                                        value: selectedItems.length,
                                    },
                                )
                            ) : (
                                t(
                                    'screens:phoneBook.deletePhoneBookDescription',
                                )
                            )
                        }
                        dialogActionsButtons={[
                            <DialogButton
                                key="cancel"
                                label={t('common:cancel')}
                                onClick={closeRemoveModal}
                            />,
                            <DialogButton
                                key="delete"
                                label={t('common:delete')}
                                className={classes.primaryModalButton}
                                onClick={
                                    selectedItems.length === 0 && rowToDelete
                                        ? () =>
                                            deleteSinglePhoneBook(rowToDelete)
                                        : () => deletePhoneBooks()
                                }
                            />,
                        ]}
                    />

                    <CallRemoveBottomBox
                        numberOfItems={selectedItems.length}
                        onDelete={() => setRemoveDialogVisible(true)}
                    />
                </div>
            </div>
        </PermissionPlaceholder>
    );
};
